import React, { useState, useEffect } from "react";
import { ChevronDown, Menu, X } from "lucide-react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownStates, setDropdownStates] = useState({
    products: false,
    services: false,
    about: false,
  });
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleDropdown = (dropdown) => {
    setDropdownStates((prev) => ({
      products: false,
      services: false,
      about: false,
      [dropdown]: !prev[dropdown],
    }));
  };

  const closeAllDropdowns = () => {
    setDropdownStates({
      products: false,
      services: false,
      about: false,
    });
  };

  const handleMobileMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
    closeAllDropdowns();
  };

  // Bifurcated product items
  const productItems = {
    fintech: [
      { name: 'Payin Solutions', href: '/PaymentSolutionsLanding' },
      { name: 'Payout Solutions', href: '/PayoutProcessLanding' },
      { name: 'NETC Acquirer', href: '/AcquirerServicesPage' },
      { name: 'NETC Issuer', href: '/IssuerDashboard' },
      { name: 'Reconciliation Management', href: '/Recon' },
      { name: 'Merchant Management', href: '/Merchant' },
    ],
    nonFintech: [
      { name: 'Video Communication', href: '/HubLiveLanding' },
      { name: 'Enterprise Content Management', href: '/dms' },


    ]
  };

  // Bifurcated service items
  const serviceItems = {
    nonFintech: [
      { name: 'Engagement Models', href: '/staff-augmentation' }, 
      { name: 'Cyber Security', href: '/cybersecurity' },
      { name: 'Data Analytics', href: '/data-analytics' },
      { name: 'HealthCare Services', href: '/payxlanding' },
      { name: 'Application Development', href: '/app-services' },
      { name: 'Application Integrations', href: '/developer' },
    ]
  };

  return (
    <header 
      className={`bg-white/95 backdrop-blur-md sticky top-0 z-50 transition-all duration-500 ease-out ${
        isScrolled ? 'shadow-md shadow-blue-500/5 border-b border-blue-100/30' : 'shadow-sm shadow-gray-100/30'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center transform transition-all duration-300 ease-out hover:scale-105">
            <a href="/" className="flex items-center">
              <img
                src="./logo.png"
                alt="AnviTech Solutions"
                className="w-64 h-auto filter transition-all duration-300 ease-out hover:brightness-110"
              />
            </a>
          </div>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden md:flex space-x-8 items-center justify-center flex-1">
            {/* Products Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => toggleDropdown("products")}
              onMouseLeave={closeAllDropdowns}
            >
              <button
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 py-2 transition-all duration-300 ease-out relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-blue-600 after:to-blue-700 after:transition-all after:duration-300 after:ease-out hover:after:w-full group"
                onClick={() => toggleDropdown("products")}
              >
                <span className="font-medium tracking-wide">PRODUCTS</span>
                <ChevronDown className={`w-4 h-4 transition-all duration-300 ease-out ${
                  dropdownStates.products ? 'rotate-180 text-blue-600' : 'group-hover:rotate-12'
                }`} />
              </button>
              
              <div className="absolute top-full left-0 w-full h-3" />
              
              <div 
                className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-3 w-[560px] bg-white/95 backdrop-blur-md rounded-2xl shadow-lg border border-gray-100/50 py-6 transition-all duration-500 ease-out origin-top ${
                  dropdownStates.products 
                    ? 'opacity-100 scale-100 translate-y-0' 
                    : 'opacity-0 scale-95 -translate-y-4 pointer-events-none'
                }`}
              >
                <div className="grid grid-cols-2 gap-8 px-6">
                  {/* Fintech Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gradient-to-r from-blue-200 to-blue-300 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                      Fintech Solutions
                    </h3>
                    <div className="space-y-1">
                      {productItems.fintech.map((item, index) => (
                        <a
                          key={item.name}
                          href={item.href}
                          className="block px-3 py-2.5 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm"
                          style={{ 
                            animationDelay: `${index * 50}ms`,
                            animation: dropdownStates.products ? 'fadeInUp 0.3s ease-out forwards' : 'none'
                          }}
                        >
                          {item.name}
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Non-Fintech Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gradient-to-r from-green-200 to-green-300 bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                      Technology Solutions
                    </h3>
                    <div className="space-y-1">
                      {productItems.nonFintech.map((item, index) => (
                        <a
                          key={item.name}
                          href={item.href}
                          className="block px-3 py-2.5 text-sm text-gray-700 hover:text-green-600 hover:bg-green-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm"
                          style={{ 
                            animationDelay: `${index * 50}ms`,
                            animation: dropdownStates.products ? 'fadeInUp 0.3s ease-out forwards' : 'none'
                          }}
                        >
                          {item.name}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Services Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => toggleDropdown("services")}
              onMouseLeave={closeAllDropdowns}
            >
              <button
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 py-2 transition-all duration-300 ease-out relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-blue-600 after:to-blue-700 after:transition-all after:duration-300 after:ease-out hover:after:w-full group"
                onClick={() => toggleDropdown("services")}
              >
                <span className="font-medium tracking-wide">SERVICES</span>
                <ChevronDown className={`w-4 h-4 transition-all duration-300 ease-out ${
                  dropdownStates.services ? 'rotate-180 text-blue-600' : 'group-hover:rotate-12'
                }`} />
              </button>
              
              <div className="absolute top-full left-0 w-full h-3" /> 
              
              <div 
                className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-3 w-64 bg-white/95 backdrop-blur-md rounded-2xl shadow-lg border border-gray-100/50 py-4 transition-all duration-500 ease-out origin-top ${
                  dropdownStates.services 
                    ? 'opacity-100 scale-100 translate-y-0' 
                    : 'opacity-0 scale-95 -translate-y-4 pointer-events-none'
                }`}
              > 
                <div className="px-3 space-y-1">
                  {serviceItems.nonFintech.map((item, index) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block py-2.5 px-3 text-sm text-gray-700 hover:text-green-600 hover:bg-green-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm"
                      style={{ 
                        animationDelay: `${index * 50}ms`,
                        animation: dropdownStates.services ? 'fadeInUp 0.3s ease-out forwards' : 'none'
                      }}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <a
              href="/industry"
              className="text-gray-700 hover:text-blue-600 py-2 transition-all duration-300 ease-out relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-blue-600 after:to-blue-700 after:transition-all after:duration-300 after:ease-out hover:after:w-full font-medium tracking-wide transform hover:scale-105"
            >
              INDUSTRIES WE SERVE
            </a>

            {/* About Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => toggleDropdown("about")}
              onMouseLeave={closeAllDropdowns}
            >
              <button
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 py-2 transition-all duration-300 ease-out relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-blue-600 after:to-blue-700 after:transition-all after:duration-300 after:ease-out hover:after:w-full group"
                onClick={() => toggleDropdown("about")}
              >
                <span className="font-medium tracking-wide">ABOUT</span>
                <ChevronDown className={`w-4 h-4 transition-all duration-300 ease-out ${
                  dropdownStates.about ? 'rotate-180 text-blue-600' : 'group-hover:rotate-12'
                }`} />
              </button>
              
              <div className="absolute top-full left-0 w-full h-3" />
              
              <div 
                className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-3 w-52 bg-white/95 backdrop-blur-md rounded-2xl shadow-lg border border-gray-100/50 py-3 transition-all duration-500 ease-out origin-top ${
                  dropdownStates.about 
                    ? 'opacity-100 scale-100 translate-y-0' 
                    : 'opacity-0 scale-95 -translate-y-4 pointer-events-none'
                }`}
              >
                <div className="px-3 space-y-1">
                  {[
                    { name: 'About Us', href: '/Aboutus' },
                    { name: 'Careers', href: '/careers' },
                    { name: 'Contact', href: '/Contact' }
                  ].map((item, index) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50/70 hover:text-blue-600 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm"
                      style={{ 
                        animationDelay: `${index * 50}ms`,
                        animation: dropdownStates.about ? 'fadeInUp 0.3s ease-out forwards' : 'none'
                      }}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* Login Button Area (kept for future use) */}
          <div className="hidden md:flex items-center">
            {/* Commented out login button */}
            {/* <a
              href="https://uat.epaisaa.com/Merchant/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-full text-sm font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 ease-out transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25">
                LOGIN
              </button>
            </a> */}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-xl hover:bg-gray-100/80 transition-all duration-300 ease-out hover:shadow-sm transform hover:scale-105"
            onClick={handleMobileMenuToggle}
          >
            <div className="relative w-6 h-6">
              <Menu className={`w-6 h-6 absolute transition-all duration-500 ease-out ${
                isMenuOpen ? 'opacity-0 rotate-180 scale-50' : 'opacity-100 rotate-0 scale-100'
              }`} />
              <X className={`w-6 h-6 absolute transition-all duration-500 ease-out ${
                isMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-180 scale-50'
              }`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100/50 overflow-hidden transition-all duration-700 ease-out ${
        isMenuOpen ? 'max-h-screen opacity-100 shadow-lg' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-4 py-4 space-y-2">
          {/* Mobile Products */}
          <div className="border-b border-gray-100/50 pb-3">
            <button
              className="w-full flex items-center justify-between py-3 text-gray-700 font-medium hover:text-blue-600 transition-all duration-300 ease-out rounded-lg hover:bg-blue-50/50"
              onClick={() => toggleDropdown("products")}
            >
              <span className="tracking-wide">PRODUCTS</span>
              <ChevronDown
                className={`w-4 h-4 transform transition-all duration-500 ease-out ${
                  dropdownStates.products ? "rotate-180 text-blue-600" : ""
                }`}
              />
            </button>
            <div className={`overflow-hidden transition-all duration-700 ease-out ${
              dropdownStates.products ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <div className="pl-4 pt-3 space-y-4">
                {/* Mobile Fintech Products */}
                <div>
                  <h4 className="text-xs font-bold text-blue-600 mb-3 uppercase tracking-wide bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                    Fintech Solutions
                  </h4>
                  <div className="space-y-1">
                    {productItems.fintech.map((item, index) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="block py-2.5 px-3 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm"
                        style={{ 
                          animationDelay: `${index * 50}ms`,
                          animation: dropdownStates.products ? 'fadeInUp 0.3s ease-out forwards' : 'none'
                        }}
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                </div>

                {/* Mobile Non-Fintech Products */}
                <div>
                  <h4 className="text-xs font-bold text-green-600 mb-3 uppercase tracking-wide bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                    Technology Solutions
                  </h4>
                  <div className="space-y-1">
                    {productItems.nonFintech.map((item, index) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="block py-2.5 px-3 text-sm text-gray-600 hover:text-green-600 hover:bg-green-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm"
                        style={{ 
                          animationDelay: `${index * 50}ms`,
                          animation: dropdownStates.products ? 'fadeInUp 0.3s ease-out forwards' : 'none'
                        }}
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Services */}
          <div className="border-b border-gray-100/50 pb-3">
            <button
              className="w-full flex items-center justify-between py-3 text-gray-700 font-medium hover:text-blue-600 transition-all duration-300 ease-out rounded-lg hover:bg-blue-50/50"
              onClick={() => toggleDropdown("services")}
            >
              <span className="tracking-wide">SERVICES</span>
              <ChevronDown
                className={`w-4 h-4 transform transition-all duration-500 ease-out ${
                  dropdownStates.services ? "rotate-180 text-blue-600" : ""
                }`}
              />
            </button>
            <div className={`overflow-hidden transition-all duration-700 ease-out ${
              dropdownStates.services ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <div className="pl-4 pt-3 space-y-1">
                {serviceItems.nonFintech.map((item, index) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block py-2.5 px-3 text-sm text-gray-600 hover:text-green-600 hover:bg-green-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm"
                    style={{ 
                      animationDelay: `${index * 50}ms`,
                      animation: dropdownStates.services ? 'fadeInUp 0.3s ease-out forwards' : 'none'
                    }}
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="border-b border-gray-100/50 pb-3">
            <a
              href="/industry"
              className="block py-3 text-gray-700 font-medium hover:text-blue-600 transition-all duration-300 ease-out rounded-lg hover:bg-blue-50/50 tracking-wide"
            >
              INDUSTRIES WE SERVE
            </a>
          </div>

          {/* Mobile About */}
          <div className="border-b border-gray-100/50 pb-3">
            <button
              className="w-full flex items-center justify-between py-3 text-gray-700 font-medium hover:text-blue-600 transition-all duration-300 ease-out rounded-lg hover:bg-blue-50/50"
              onClick={() => toggleDropdown("about")}
            >
              <span className="tracking-wide">ABOUT</span>
              <ChevronDown
                className={`w-4 h-4 transform transition-all duration-500 ease-out ${
                  dropdownStates.about ? "rotate-180 text-blue-600" : ""
                }`}
              />
            </button>
            <div className={`overflow-hidden transition-all duration-700 ease-out ${
              dropdownStates.about ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <div className="pl-4 pt-3 space-y-1">
                {[
                  { name: 'About Us', href: '/Aboutus' }, 
                  { name: 'Careers', href: '/careers' },
                  { name: 'Contact', href: '/Contact' }
                 

                ].map((item, index) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block py-2.5 px-3 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm"
                    style={{ 
                      animationDelay: `${index * 50}ms`,
                      animation: dropdownStates.about ? 'fadeInUp 0.3s ease-out forwards' : 'none'
                    }}
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </header>
  );
};

export default Header;